import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { reservationService, MyReservation } from 'services/reservationService';
import {
  Body,
  Header,
  BackButton,
  DateSection,
  DateHeader,
  ReservationItem,
  ReservationCard,
  TimeSection,
  TimePeriod,
  Time,
  Divider,
  FacilityName,
  FacilityDetail,
  DetailIcon,
  EmptyMessage
} from './ReservationHistory.styles';

interface ReservationCardProps {
  reservation: MyReservation;
  onClick: () => void;
}

const ReservationCardComponent: React.FC<ReservationCardProps> = ({ reservation, onClick }) => {
  const formatTime = (isoString: string): { period: string; time: string } => {
    const date = new Date(isoString);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const time = `${displayHours}:${minutes.toString().padStart(2, '0')}`;
    return { period, time };
  };

  const startTimeFormatted = formatTime(reservation.startTime);

  // Calculate end time
  const endTime = new Date(new Date(reservation.startTime).getTime() + reservation.durationMinutes * 60 * 1000);
  const endTimeFormatted = formatTime(endTime.toISOString());

  const locationText = [
    reservation.facility.building?.name,
    reservation.facility.location
  ].filter(Boolean).join(' ');

  return (
    <ReservationItem onClick={onClick}>
      <TimeSection>
        <TimePeriod>{startTimeFormatted.period}</TimePeriod>
        <Time>{startTimeFormatted.time}</Time>
        <div style={{ margin: '2px 0', color: '#999', fontSize: '10px', lineHeight: 1 }}>~</div>
        <TimePeriod>{endTimeFormatted.period}</TimePeriod>
        <Time>{endTimeFormatted.time}</Time>
      </TimeSection>
      <Divider />
      <ReservationCard>
        <FacilityName>{reservation.facility.name}</FacilityName>
        {locationText && (
          <FacilityDetail>
            <DetailIcon>📍</DetailIcon>
            {locationText}
          </FacilityDetail>
        )}
        <FacilityDetail>
          <DetailIcon>👥</DetailIcon>
          {reservation.headCount}명
        </FacilityDetail>
      </ReservationCard>
    </ReservationItem>
  );
};

function ReservationHistory() {
  const navigate = useNavigate();
  const [reservationData, setReservationData] = useState<MyReservation[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchReservations = async () => {
      try {
        const data = await reservationService.getMyReservations();
        setReservationData(data);
      } catch (error) {
        console.error('Error fetching reservations:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchReservations();
  }, []);

  const handleGoBack = () => {
    navigate(-1);
  };

  // Group reservations by date
  const groupedReservations = reservationData.reduce((groups, reservation) => {
    const date = new Date(reservation.startTime).toISOString().split('T')[0];
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(reservation);
    return groups;
  }, {} as Record<string, MyReservation[]>);

  // Sort dates in descending order
  const sortedDates = Object.keys(groupedReservations).sort((a, b) => b.localeCompare(a));

  const formatDateHeader = (dateString: string): string => {
    const date = new Date(dateString);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const dayNames = ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일'];
    const dayName = dayNames[date.getDay()];

    return `${month}월 ${day}일 ${dayName.charAt(0)}요일`;
  };

  if (isLoading) {
    return (
      <Body>
        <Header>
          <BackButton onClick={handleGoBack}>
            <FontAwesomeIcon icon={faChevronLeft} />
          </BackButton>
          예약 내역
        </Header>
        <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>
      </Body>
    );
  }

  return (
    <Body>
      <Header>
        <BackButton onClick={handleGoBack}>
          <FontAwesomeIcon icon={faChevronLeft} />
        </BackButton>
        예약 내역
      </Header>

      {sortedDates.length > 0 ? (
        sortedDates.map((date) => (
          <DateSection key={date}>
            <DateHeader>{formatDateHeader(date)}</DateHeader>
            {groupedReservations[date].map((reservation) => (
              <ReservationCardComponent
                key={reservation.id}
                reservation={reservation}
                onClick={() => navigate(`/reservations/${reservation.id}`)}
              />
            ))}
          </DateSection>
        ))
      ) : (
        <EmptyMessage>예약 내역이 없습니다.</EmptyMessage>
      )}
    </Body>
  );
}

export default ReservationHistory;
